
// Vendordersdetails.jsx
import React, { useState, useEffect } from "react";
import {
    getCateringOrders,
    getCateringVendors,
    approveCateringOrder
} from "../../../Servicefile/ordermanagementservice.jsx";
import { 
    FaEye, FaEdit, FaTrash, FaCheck, FaTimes, 
    FaFilePdf, FaPrint, FaSearch, FaFilter,
    FaCheckCircle, FaClock, FaExclamationCircle,
    FaDollarSign, FaUser, FaCalendarAlt, FaMapMarkerAlt,
    FaBuilding, FaPhone, FaEnvelope, FaPaperPlane,
    FaPlus, FaSpinner, FaSync, FaChevronDown, FaChevronRight,
    FaUtensils, FaClipboardList, FaUsers
} from 'react-icons/fa';

const Vendordersdetails = () => {
    const [orders, setOrders] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState('');
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showSendModal, setShowSendModal] = useState(false);
    const [selectedOrderForSend, setSelectedOrderForSend] = useState(null);
    const [selectedVendors, setSelectedVendors] = useState({});
    const [selectAll, setSelectAll] = useState(false);
    const [sendingEnquiry, setSendingEnquiry] = useState(false);
    const [vendorSearchTerm, setVendorSearchTerm] = useState('');
    const [paymentAmount, setPaymentAmount] = useState(0);
    const [leadPrice, setLeadPrice] = useState(0);
    
    // Date filter states
    const [dateFilter, setDateFilter] = useState('all');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [showCustomDateModal, setShowCustomDateModal] = useState(false);

    useEffect(() => {
        fetchOrders();
        fetchVendors();
    }, []);

    useEffect(() => {
        if (showSuccessPopup) {
            const timer = setTimeout(() => {
                setShowSuccessPopup(false);
                setSuccessMessage("");
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showSuccessPopup]);

    // Date filter helper functions
    const getDateRange = (filterType) => {
        const today = new Date();
        const start = new Date();
        const end = new Date();

        switch (filterType) {
            case 'today':
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'yesterday':
                start.setDate(today.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                end.setDate(today.getDate() - 1);
                end.setHours(23, 59, 59, 999);
                break;
            case 'week':
                const day = today.getDay();
                start.setDate(today.getDate() - day);
                start.setHours(0, 0, 0, 0);
                end.setDate(today.getDate() + (6 - day));
                end.setHours(23, 59, 59, 999);
                break;
            case 'month':
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'custom':
                if (customStartDate && customEndDate) {
                    return { from: new Date(customStartDate), to: new Date(customEndDate) };
                }
                return null;
            default:
                return null;
        }
        return { from: start, to: end };
    };

    const applyDateFilter = (ordersList) => {
        if (dateFilter === 'all') return ordersList;

        const range = getDateRange(dateFilter);
        if (!range) return ordersList;

        return ordersList.filter(order => {
            // Use eventDate or createdAt for filtering
            const dateStr = order.eventDate || order.createdAt;
            if (!dateStr) return false;
            const orderDate = new Date(dateStr);
            return orderDate >= range.from && orderDate <= range.to;
        });
    };

    const getDateFilterLabel = () => {
        switch (dateFilter) {
            case 'all': return 'All Dates';
            case 'today': return 'Today';
            case 'yesterday': return 'Yesterday';
            case 'week': return 'This Week';
            case 'month': return 'This Month';
            case 'custom': return 'Custom Range';
            default: return 'All Dates';
        }
    };

    const fetchOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) throw new Error("No auth token found");
            const data = await getCateringOrders(authToken);
            // Filter: only show orders where adminPermision is false (not yet approved)
            const filteredData = Array.isArray(data) ? data.filter(item => item.adminPermision === false && item.adminId === null) : [];
            setOrders(filteredData);
            localStorage.setItem("vendOrdersData", JSON.stringify(filteredData));
        } catch (err) {
            console.error("Error fetching orders:", err);
            setError("Failed to load orders.");
        } finally {
            setLoading(false);
        }
    };

    const fetchVendors = async () => {
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) return;
            const data = await getCateringVendors(authToken);
            const processedVendors = Array.isArray(data) ? data.map(vendor => ({
                id: vendor.vendorId || vendor.id,
                companyName: vendor.registeredName || vendor.companyName || vendor.name || "Company Name Not Available",
                vendorId: vendor.vendorId,
                city: vendor.city || 'N/A',
                mobileNumber: vendor.mobileNumber || 'N/A'
            })) : [];
            setVendors(processedVendors);
        } catch (err) {
            console.error("Error fetching vendors:", err);
        }
    };

    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setShowViewModal(true);
    };

    const handleSendToVendors = (order) => {
        setSelectedOrderForSend(order);
        setSelectedVendors({});
        setSelectAll(false);
        setVendorSearchTerm('');
        setPaymentAmount(order.paymentAmount || 0);
        setLeadPrice(order.budget || 0);
        setShowSendModal(true);
    };

    const handleVendorSelect = (vendorId) => {
        setSelectedVendors(prev => {
            const newState = { ...prev };
            if (newState[vendorId]) {
                delete newState[vendorId];
            } else {
                newState[vendorId] = true;
            }
            return newState;
        });
        setSelectAll(false);
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedVendors({});
        } else {
            const allVendorIds = {};
            filteredVendors.forEach(v => allVendorIds[v.id] = true);
            setSelectedVendors(allVendorIds);
        }
        setSelectAll(!selectAll);
    };

    const handleSendToSelectedVendors = async () => {
        const selectedIds = Object.keys(selectedVendors).map(Number);
        if (selectedIds.length === 0) {
            alert('Please select at least one vendor');
            return;
        }

        setSendingEnquiry(true);
        try {
            const authToken = localStorage.getItem("authToken");
            const orderId = selectedOrderForSend.id || selectedOrderForSend.orderId;
            
            const payload = {
                adminPermision: true,
                paymentAmount: paymentAmount || 0,
                vendorIds: selectedIds,
                assignToAll: false
            };

            await approveCateringOrder(orderId, payload, authToken);
            
            setSuccessMessage(`✅ Order sent to ${selectedIds.length} vendor(s) successfully!`);
            setShowSuccessPopup(true);
            setShowSendModal(false);
            
            // Remove the order from the list
            setOrders(prev => prev.filter(item => (item.id !== orderId && item.orderId !== orderId)));
            
            setSelectedOrderForSend(null);
            setSelectedVendors({});
        } catch (error) {
            console.error('Error sending enquiry:', error);
            setSuccessMessage("❌ " + (error.response?.data?.message || 'Failed to send to vendors'));
            setShowSuccessPopup(true);
        } finally {
            setSendingEnquiry(false);
        }
    };

    const handleSendToAllVendors = async () => {
        setSendingEnquiry(true);
        try {
            const authToken = localStorage.getItem("authToken");
            const orderId = selectedOrderForSend.id || selectedOrderForSend.orderId;
            
            const payload = {
                adminPermision: true,
                paymentAmount: paymentAmount || 0,
                vendorIds: null,
                assignToAll: true
            };

            await approveCateringOrder(orderId, payload, authToken);
            
            setSuccessMessage(`✅ Order sent to all vendors successfully!`);
            setShowSuccessPopup(true);
            setShowSendModal(false);
            
            // Remove the order from the list
            setOrders(prev => prev.filter(item => (item.id !== orderId && item.orderId !== orderId)));
            
            setSelectedOrderForSend(null);
            setSelectedVendors({});
        } catch (error) {
            console.error('Error sending to all vendors:', error);
            setSuccessMessage("❌ " + (error.response?.data?.message || 'Failed to send to all vendors'));
            setShowSuccessPopup(true);
        } finally {
            setSendingEnquiry(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return "N/A";
        try {
            return new Date(`1970-01-01T${timeString}`).toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return timeString;
        }
    };

    const getTotalPlates = (order) => {
        return (order.vegPlates || 0) + (order.nonVegPlates || 0) + (order.mixedPlates || 0);
    };

    const formatLocation = (order) => {
        const parts = [];
        if (order.fullAddress) parts.push(order.fullAddress);
        if (order.city) parts.push(order.city);
        if (order.state) parts.push(order.state);
        if (order.pincode) parts.push(order.pincode);
        if (order.country) parts.push(order.country);
        return parts.join(', ');
    };

    const isNonVeg = (item) => {
        if (!item || typeof item !== 'string') return false;
        const nonVegKeywords = ['chicken', 'chiken', 'mutton', 'fish', 'egg', 'prawn', 'meat', 'beef', 'pork'];
        return nonVegKeywords.some(keyword => item.toLowerCase().includes(keyword));
    };

    const formatAddOnName = (addOnType) => {
        if (!addOnType) return '';
        return addOnType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'PENDING': { bg: '#FEF3C7', color: '#92400E', label: 'Pending', icon: <FaClock size={10} /> },
            'IN_PROGRESS': { bg: '#DBEAFE', color: '#1E40AF', label: 'In Progress', icon: <FaSync size={10} /> },
            'QUOTED': { bg: '#E0E7FF', color: '#3730A3', label: 'Quoted', icon: <FaCheckCircle size={10} /> },
            'APPROVED': { bg: '#D1FAE5', color: '#065F46', label: 'Approved', icon: <FaCheckCircle size={10} /> },
            'REJECTED': { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected', icon: <FaTimes size={10} /> },
            'CLOSED': { bg: '#F3F4F6', color: '#4B5563', label: 'Closed', icon: <FaTimes size={10} /> }
        };
        const statusInfo = statusMap[status] || statusMap['PENDING'];
        return (
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: 600,
                background: statusInfo.bg,
                color: statusInfo.color
            }}>
                {statusInfo.icon} {statusInfo.label}
            </span>
        );
    };

    // Calculate stats - with date filter applied
    const filteredOrdersForStats = applyDateFilter(orders);
    const totalOrders = filteredOrdersForStats.length;
    const totalPlates = filteredOrdersForStats.reduce((sum, order) => sum + getTotalPlates(order), 0);
    const totalBudget = filteredOrdersForStats.reduce((sum, order) => sum + (order.budget || 0), 0);

    // Filter vendors
    const filteredVendors = vendors.filter(vendor =>
        (vendor.companyName || '').toLowerCase().includes(vendorSearchTerm.toLowerCase())
    );

    // Filter orders - apply search AND date filter
    const filteredOrders = applyDateFilter(orders).filter(order => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (order.fullName || '').toLowerCase().includes(search) ||
               (order.email || '').toLowerCase().includes(search) ||
               (order.city || '').toLowerCase().includes(search) ||
               (order.eventType || '').toLowerCase().includes(search);
    });

    if (loading) return (
        <div style={loadingContainerStyle}>
            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={32} color="#e66d33" />
            <p>Loading orders...</p>
        </div>
    );
    
    if (error) return (
        <div style={errorContainerStyle}>
            <FaExclamationCircle size={32} color="#DC2626" />
            <p>{error}</p>
        </div>
    );

    return (
        <div style={containerStyle}>
            {/* Success Popup */}
            {showSuccessPopup && (
                <div style={{
                    ...popupStyle,
                    ...(successMessage.includes('❌') ? popupErrorStyle : popupSuccessStyle)
                }}>
                    <span style={{ fontSize: '20px' }}>{successMessage.includes('✅') ? '✓' : '✗'}</span>
                    <span style={{ fontWeight: 'bold' }}>{successMessage}</span>
                </div>
            )}

          

            {/* Stats Cards */}
            <div style={statsContainerStyle}>
                <div style={statCardStyle}>
                    <div style={{...statIconWrapperStyle, background: '#EEF2FF'}}>
                        <FaClipboardList style={{color: '#4F46E5'}} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{totalOrders}</div>
                        <div style={statLabelStyle}>Total Orders</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{...statIconWrapperStyle, background: '#FEF3C7'}}>
                        <FaUtensils style={{color: '#D97706'}} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{totalPlates}</div>
                        <div style={statLabelStyle}>Total Plates</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{...statIconWrapperStyle, background: '#D1FAE5'}}>
                        <FaDollarSign style={{color: '#059669'}} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>₹{totalBudget.toLocaleString()}</div>
                        <div style={statLabelStyle}>Total Budget</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{...statIconWrapperStyle, background: '#F3F4F6'}}>
                        <FaUsers style={{color: '#6B7280'}} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{vendors.length}</div>
                        <div style={statLabelStyle}>Total Vendors</div>
                    </div>
                </div>
            </div>

            {/* Search and Date Filter */}
            <div style={searchFilterContainerStyle}>
                <div style={searchContainerStyle}>
                    <FaSearch style={searchIconStyle} />
                    <input
                        type="text"
                        placeholder="Search by customer, email, city..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={searchInputStyle}
                    />
                </div>

                {/* Date Filter */}
                <div style={dateFilterContainerStyle}>
                    <FaCalendarAlt style={filterIconStyle} />
                    <select
                        value={dateFilter}
                        onChange={(e) => {
                            const value = e.target.value;
                            setDateFilter(value);
                            if (value === 'custom') {
                                setShowCustomDateModal(true);
                            }
                        }}
                        style={filterSelectStyle}
                    >
                        <option value="all">All Dates</option>
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="custom">Custom Range</option>
                    </select>
                    <FaChevronDown style={{ color: '#9CA3AF', fontSize: '10px', marginLeft: '4px' }} />
                </div>

                {/* Custom Date Modal */}
                {showCustomDateModal && (
                    <div style={customDateModalOverlay} onClick={() => setShowCustomDateModal(false)}>
                        <div style={customDateModalStyle} onClick={(e) => e.stopPropagation()}>
                            <div style={customDateModalHeader}>
                                <h3 style={customDateModalTitle}>Select Custom Date Range</h3>
                                <button
                                    onClick={() => setShowCustomDateModal(false)}
                                    style={customDateModalClose}
                                >
                                    <FaTimes />
                                </button>
                            </div>
                            <div style={customDateModalBody}>
                                <div style={customDateModalInputGroup}>
                                    <label style={customDateModalLabel}>From Date</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        style={customDateModalInput}
                                        max={customEndDate || undefined}
                                    />
                                </div>
                                <div style={customDateModalInputGroup}>
                                    <label style={customDateModalLabel}>To Date</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        style={customDateModalInput}
                                        min={customStartDate || undefined}
                                    />
                                </div>
                            </div>
                            <div style={customDateModalFooter}>
                                <button
                                    onClick={() => {
                                        setShowCustomDateModal(false);
                                        setCustomStartDate('');
                                        setCustomEndDate('');
                                    }}
                                    style={customDateModalCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (customStartDate && customEndDate) {
                                            setDateFilter('custom');
                                            setShowCustomDateModal(false);
                                        } else {
                                            alert('Please select both start and end dates');
                                        }
                                    }}
                                    style={customDateModalApply}
                                >
                                    Apply Range
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Table */}
            <div style={tableCardStyle}>
                <div style={tableWrapperStyle}>
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={{...thStyle, width: '4%', textAlign: 'center'}}>S.No</th>
                                <th style={{...thStyle, width: '12%', textAlign: 'left'}}>Customer</th>
                                <th style={{...thStyle, width: '8%', textAlign: 'center'}}>Event</th>
                                <th style={{...thStyle, width: '10%', textAlign: 'center'}}>Date</th>
                                <th style={{...thStyle, width: '8%', textAlign: 'center'}}>Time</th>
                                <th style={{...thStyle, width: '7%', textAlign: 'center'}}>Plates</th>
                                <th style={{...thStyle, width: '9%', textAlign: 'center'}}>Budget</th>
                                <th style={{...thStyle, width: '9%', textAlign: 'center'}}>Status</th>
                                <th style={{...thStyle, width: '33%', textAlign: 'center'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.length === 0 ? (
                                <tr>
                                    <td colSpan="9" style={emptyStateStyle}>
                                        <div style={emptyStateContentStyle}>
                                            <FaClipboardList size={40} color="#D1D5DB" />
                                            <p>No pending orders found</p>
                                            <span style={{ fontSize: '13px', color: '#9CA3AF' }}>
                                                {orders.length === 0 ? 'No orders available' : 'Try adjusting your search or filter'}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredOrders.map((order, index) => {
                                    const orderId = order.id || order.orderId;
                                    const totalPlates = getTotalPlates(order);

                                    return (
                                        <tr key={orderId} style={tableRowStyle}>
                                            <td style={{...tdStyle, textAlign: 'center', fontWeight: 500, color: '#9CA3AF'}}>
                                                {index + 1}
                                            </td>
                                            <td style={{...tdStyle, textAlign: 'left'}}>
                                                <div style={customerCellStyle}>
                                                    <div style={avatarStyle}>
                                                        {(order.fullName || '?')[0].toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                                                            {order.fullName || order.name || '-'}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                                            {order.email || '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{...tdStyle, textAlign: 'center'}}>
                                                <span style={eventBadgeStyle}>
                                                    {order.eventType || '-'}
                                                </span>
                                            </td>
                                            <td style={{...tdStyle, textAlign: 'center', fontSize: '13px', color: '#374151'}}>
                                                {order.event === 'EVENT' 
                                                    ? formatDate(order.eventDate)
                                                    : `${formatDate(order.fromDate)} - ${formatDate(order.toDate)}`}
                                            </td>
                                            <td style={{...tdStyle, textAlign: 'center', fontSize: '13px', color: '#374151'}}>
                                                {order.eventTime ? formatTime(order.eventTime) : 'N/A'}
                                            </td>
                                            <td style={{...tdStyle, textAlign: 'center'}}>
                                                <span style={platesBadgeStyle}>
                                                    {totalPlates}
                                                </span>
                                            </td>
                                            <td style={{...tdStyle, textAlign: 'center', fontWeight: 600, color: '#059669'}}>
                                                ₹{Number(order.budget || 0).toLocaleString()}
                                            </td>
                                            <td style={{...tdStyle, textAlign: 'center'}}>
                                                {getStatusBadge(order.leadStatus || 'PENDING')}
                                            </td>
                                            <td style={{...tdStyle, textAlign: 'center'}}>
                                                <div style={actionGroupStyle}>
                                                    <button
                                                        style={viewButtonStyle}
                                                        onClick={() => handleViewOrder(order)}
                                                        title="View"
                                                    >
                                                        <FaEye size={13} />
                                                    </button>
                                                    <button
                                                        style={sendButtonStyle}
                                                        onClick={() => handleSendToVendors(order)}
                                                        title="Send to Vendors"
                                                    >
                                                        <FaPaperPlane size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View Order Modal */}
            {showViewModal && selectedOrder && (
                <div style={modalOverlayStyle} onClick={() => setShowViewModal(false)}>
                    <div style={{...modalStyle, maxWidth: '800px'}} onClick={(e) => e.stopPropagation()}>
                        <div style={modalHeaderStyle}>
                            <div style={modalHeaderLeftStyle}>
                                <div style={modalHeaderIconStyle}>
                                    <FaClipboardList size={18} />
                                </div>
                                <h3 style={modalTitleStyle}>
                                    Order #{selectedOrder.id || selectedOrder.orderId}
                                </h3>
                            </div>
                            <button onClick={() => setShowViewModal(false)} style={modalCloseStyle}>
                                <FaTimes />
                            </button>
                        </div>

                        <div style={modalBodyStyle}>
                            {/* Customer Info */}
                            <div style={quotationHeaderStyle}>
                                <div>
                                    <h4 style={{margin: 0, fontSize: '16px', fontWeight: 700, color: '#111827'}}>
                                        {selectedOrder.fullName || selectedOrder.name}
                                    </h4>
                                    <p style={{margin: '2px 0', fontSize: '14px', color: '#6B7280'}}>
                                        {selectedOrder.companyName || 'N/A'}
                                    </p>
                                    <div style={contactInfoStyle}>
                                        <span><FaEnvelope size={12} /> {selectedOrder.email || 'N/A'}</span>
                                        <span><FaPhone size={12} /> {selectedOrder.phoneNumber || selectedOrder.mobile || 'N/A'}</span>
                                    </div>
                                </div>
                                <div>
                                    <div style={quotationMetaStyle}>
                                        <div><strong>Status:</strong> {getStatusBadge(selectedOrder.leadStatus || 'PENDING')}</div>
                                        <div><strong>Budget:</strong> ₹{Number(selectedOrder.budget || 0).toLocaleString()}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Event Details */}
                            <div style={quotationEventStyle}>
                                <div style={quotationEventItemStyle}>
                                    <FaCalendarAlt size={14} />
                                    <span><strong>Event:</strong> {selectedOrder.eventType || selectedOrder.event || 'N/A'}</span>
                                </div>
                                <div style={quotationEventItemStyle}>
                                    <FaCalendarAlt size={14} />
                                    <span><strong>Date:</strong> {selectedOrder.eventDate ? formatDate(selectedOrder.eventDate) : 'N/A'}</span>
                                </div>
                                {selectedOrder.eventTime && (
                                    <div style={quotationEventItemStyle}>
                                        <FaClock size={14} />
                                        <span><strong>Time:</strong> {formatTime(selectedOrder.eventTime)}</span>
                                    </div>
                                )}
                                <div style={quotationEventItemStyle}>
                                    <FaMapMarkerAlt size={14} />
                                    <span><strong>Location:</strong> {formatLocation(selectedOrder)}</span>
                                </div>
                            </div>

                            {/* Plates Summary */}
                            <div style={tableWrapperSmallStyle}>
                                <table style={subTableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={{...subThStyle, textAlign: 'center'}}>Veg Plates</th>
                                            <th style={{...subThStyle, textAlign: 'center'}}>Non-Veg Plates</th>
                                            <th style={{...subThStyle, textAlign: 'center'}}>Total Plates</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{...subTdStyle, textAlign: 'center', color: '#276749', fontWeight: 700}}>
                                                {selectedOrder.vegPlates || 0}
                                            </td>
                                            <td style={{...subTdStyle, textAlign: 'center', color: '#9b2c2c', fontWeight: 700}}>
                                                {selectedOrder.nonVegPlates || 0}
                                            </td>
                                            <td style={{...subTdStyle, textAlign: 'center', fontWeight: 700, fontSize: '16px'}}>
                                                {getTotalPlates(selectedOrder)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Menu Items */}
                            {selectedOrder.items && typeof selectedOrder.items === 'object' && Object.keys(selectedOrder.items).length > 0 && (
                                <div style={sectionContainerStyle}>
                                    <h5 style={sectionTitleStyle}>
                                        <FaUtensils size={14} /> Menu Items
                                    </h5>
                                    {Object.entries(selectedOrder.items).map(([category, items]) => {
                                        if (!Array.isArray(items) || items.length === 0) return null;
                                        return (
                                            <div key={category} style={categoryContainerStyle}>
                                                <div style={categoryTitleStyle}>
                                                    {category} ({items.length})
                                                </div>
                                                {items.map((item, idx) => {
                                                    const nonVeg = isNonVeg(item);
                                                    return (
                                                        <div key={idx} style={{...menuItemStyle, ...(nonVeg ? menuItemNonVegStyle : menuItemVegStyle)}}>
                                                            <span>{item}</span>
                                                            <span style={{...badgeStyle, ...(nonVeg ? nonVegBadgeStyle : vegBadgeStyle)}}>
                                                                {nonVeg ? 'Non-Veg' : 'Veg'}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Add-Ons */}
                            {selectedOrder.addOns && Array.isArray(selectedOrder.addOns) && selectedOrder.addOns.some(a => a.selected) && (
                                <div style={sectionContainerStyle}>
                                    <h5 style={sectionTitleStyle}>
                                        <FaCheckCircle size={14} /> Add-Ons
                                    </h5>
                                    {selectedOrder.addOns.filter(a => a.selected).map((addon, idx) => (
                                        <div key={idx} style={addOnItemStyle}>
                                            <span>{formatAddOnName(addon.addOnType)}</span>
                                            <span style={addOnQtyStyle}>
                                                Qty: {addon.quantity}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Additional Requests */}
                            {selectedOrder.additionalRequests && (
                                <div style={additionalRequestStyle}>
                                    <span style={{ fontWeight: 600 }}>📝 Additional Requests: </span>
                                    <span>{selectedOrder.additionalRequests}</span>
                                </div>
                            )}

                            <div style={formActionStyle}>
                                <button onClick={() => setShowViewModal(false)} style={cancelButtonStyle}>
                                    Close
                                </button>
                                <button onClick={() => {
                                    setShowViewModal(false);
                                    handleSendToVendors(selectedOrder);
                                }} style={sendButtonLargeStyle}>
                                    <FaPaperPlane size={14} />
                                    Send to Vendors
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Send to Vendors Modal */}
            {showSendModal && selectedOrderForSend && (
                <div style={modalOverlayStyle} onClick={() => setShowSendModal(false)}>
                    <div style={{...modalStyle, maxWidth: '600px'}} onClick={(e) => e.stopPropagation()}>
                        <div style={modalHeaderStyle}>
                            <div style={modalHeaderLeftStyle}>
                                <div style={{...modalHeaderIconStyle, background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)'}}>
                                    <FaPaperPlane size={18} />
                                </div>
                                <h3 style={modalTitleStyle}>Send to Vendors</h3>
                            </div>
                            <button onClick={() => setShowSendModal(false)} style={modalCloseStyle}>
                                <FaTimes />
                            </button>
                        </div>

                        <div style={modalBodyStyle}>
                            {/* Order Info */}
                            <div style={sendEnquiryInfoStyle}>
                                <div style={sendEnquiryInfoItemStyle}>
                                    <span style={{ fontWeight: 500 }}>Customer:</span>
                                    <span>{selectedOrderForSend.fullName || selectedOrderForSend.name}</span>
                                </div>
                                <div style={sendEnquiryInfoItemStyle}>
                                    <span style={{ fontWeight: 500 }}>Event:</span>
                                    <span>{selectedOrderForSend.eventType || selectedOrderForSend.event}</span>
                                </div>
                                <div style={sendEnquiryInfoItemStyle}>
                                    <span style={{ fontWeight: 500 }}>Budget:</span>
                                    <span style={{ color: '#059669', fontWeight: 600 }}>₹{Number(selectedOrderForSend.budget || 0).toLocaleString()}</span>
                                </div>
                            </div>

                            {/* Payment Amount - Lead Price */}
                            <div style={formGroupStyle}>
                                <label style={labelStyle}>Lead Price</label>
                                <input
                                    type="number"
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                                    style={inputStyle}
                                    placeholder="Enter lead price"
                                    min="0"
                                    step="1"
                                />
                            </div>

                            {/* Vendor Search */}
                            <div style={{...searchContainerStyle, marginBottom: '12px', marginTop: '12px'}}>
                                <FaSearch style={searchIconStyle} />
                                <input
                                    type="text"
                                    placeholder="Search vendors..."
                                    value={vendorSearchTerm}
                                    onChange={(e) => setVendorSearchTerm(e.target.value)}
                                    style={searchInputStyle}
                                />
                            </div>

                            {/* Select All */}
                            <div style={selectAllContainerStyle}>
                                <label style={selectAllLabelStyle}>
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                        style={checkboxStyle}
                                    />
                                    <span style={{ fontWeight: 600 }}>Select All Vendors</span>
                                </label>
                                <span style={{ fontSize: '12px', color: '#6B7280' }}>
                                    {Object.keys(selectedVendors).length} selected
                                </span>
                            </div>

                            {/* Send to All Button */}
                            <button
                                onClick={handleSendToAllVendors}
                                style={sendAllVendorsButtonStyle}
                                disabled={sendingEnquiry}
                            >
                                {sendingEnquiry ? (
                                    <>
                                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={14} />
                                        Sending to All...
                                    </>
                                ) : (
                                    <>
                                        <FaUsers size={14} />
                                        Send to All Vendors
                                    </>
                                )}
                            </button>

                            {/* Vendor List */}
                            <div style={vendorListStyle}>
                                {filteredVendors.length === 0 ? (
                                    <div style={noVendorsStyle}>No vendors found</div>
                                ) : (
                                    filteredVendors.map(vendor => (
                                        <label key={vendor.id} style={vendorItemStyle}>
                                            <input
                                                type="checkbox"
                                                checked={selectedVendors[vendor.id] || false}
                                                onChange={() => handleVendorSelect(vendor.id)}
                                                style={checkboxStyle}
                                            />
                                            <div>
                                                <div style={{ fontWeight: 500, fontSize: '14px' }}>
                                                    {vendor.companyName}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                                    {vendor.city || 'City not specified'} • {vendor.mobileNumber || 'No phone'}
                                                </div>
                                            </div>
                                        </label>
                                    ))
                                )}
                            </div>

                            {/* Actions */}
                            <div style={formActionStyle}>
                                <button
                                    type="button"
                                    onClick={() => setShowSendModal(false)}
                                    style={cancelButtonStyle}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSendToSelectedVendors}
                                    style={submitButtonStyle}
                                    disabled={sendingEnquiry || Object.keys(selectedVendors).length === 0}
                                >
                                    {sendingEnquiry ? (
                                        <>
                                            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={14} />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <FaPaperPlane size={14} />
                                            Send to {Object.keys(selectedVendors).length} Vendor{Object.keys(selectedVendors).length > 1 ? 's' : ''}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes fadeOut {
                    from { opacity: 1; }
                    to { opacity: 0; }
                }
                div::-webkit-scrollbar { width: 6px; height: 6px; }
                div::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
                div::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
                div::-webkit-scrollbar-thumb:hover { background: #a1a1a1; }
            `}</style>
        </div>
    );
};

// ─── Styles ──────────────────────────────────────────────────────

const containerStyle = {
    padding: '10px 0px',

};

const loadingContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '12px',
    color: '#6B7280',
};

const errorContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '12px',
    color: '#DC2626',
};

const popupStyle = {
    position: 'fixed',
    top: '20px',
    right: '20px',
    padding: '15px 25px',
    borderRadius: '8px',
    color: 'white',
    zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    animation: 'slideInRight 0.3s ease, fadeOut 0.3s ease 2.7s',
};

const popupSuccessStyle = {
    background: '#059669',
};

const popupErrorStyle = {
    background: '#DC2626',
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
};

const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
};

const headerIconStyle = {
    width: '44px',
    height: '44px',
    background: 'linear-gradient(135deg, #e66d33, #e66d33)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
};

const titleStyle = {
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
    margin: 0,
    letterSpacing: '-0.5px',
};

const subtitleStyle = {
    fontSize: '14px',
    color: '#6B7280',
    margin: '4px 0 0 0',
};

const refreshButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 18px',
    background: '#fff',
    color: '#4B5563',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    fontWeight: 500,
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const statsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '10px',
};

const statCardStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    background: '#fff',
    padding: '18px 20px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #E5E7EB',
    transition: 'all 0.2s ease',
};

const statIconWrapperStyle = {
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const statNumberStyle = {
    fontSize: '22px',
    fontWeight: 700,
    color: '#111827',
    lineHeight: 1.2,
};

const statLabelStyle = {
    fontSize: '13px',
    color: '#6B7280',
    fontWeight: 500,
};

const searchFilterContainerStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '10px',
    flexWrap: 'wrap',
    alignItems: 'center',
};

const searchContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: '200px',
    background: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    padding: '0 16px',
    transition: 'all 0.2s ease',
};

const searchIconStyle = {
    color: '#9CA3AF',
    marginRight: '10px',
};

const searchInputStyle = {
    flex: 1,
    padding: '11px 0',
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    background: 'transparent',
    color: '#111827',
};

const dateFilterContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    padding: '0 14px',
    minWidth: '160px',
};

const filterIconStyle = {
    color: '#6B7280',
    marginRight: '10px',
    fontSize: '13px',
};

const filterSelectStyle = {
    padding: '11px 4px',
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    background: 'transparent',
    cursor: 'pointer',
    color: '#111827',
    appearance: 'none',
    paddingRight: '4px',
};

// ─── Custom Date Modal Styles ──────────────────────────────────

const customDateModalOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
};

const customDateModalStyle = {
    background: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
};

const customDateModalHeader = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #F3F4F6',
};

const customDateModalTitle = {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
};

const customDateModalClose = {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6B7280',
    padding: '4px 8px',
};

const customDateModalBody = {
    padding: '24px',
};

const customDateModalInputGroup = {
    marginBottom: '16px',
};

const customDateModalLabel = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '6px',
};

const customDateModalInput = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    background: '#fff',
    color: '#111827',
    boxSizing: 'border-box',
};

const customDateModalFooter = {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '1px solid #F3F4F6',
};

const customDateModalCancel = {
    padding: '8px 20px',
    background: '#F3F4F6',
    color: '#4B5563',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
};

const customDateModalApply = {
    padding: '8px 24px',
    background: 'linear-gradient(135deg, #e66d33, #e66d33)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(230, 109, 51, 0.3)',
};

const tableCardStyle = {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    border: '1px solid #E5E7EB',
};

const tableWrapperStyle = {
    overflowX: 'auto',
    padding: '0',
    maxHeight: 'calc(90vh - 300px)',
    scrollbarWidth: 'thin',
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '1100px',
};

const thStyle = {
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    background: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
};

const tdStyle = {
    padding: '12px 16px',
    fontSize: '13px',
    color: '#111827',
    borderBottom: '1px solid #F3F4F6',
};

const tableRowStyle = {
    transition: 'background 0.15s ease',
    cursor: 'default',
};

const customerCellStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
};

const avatarStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #e66d33, #e66d33)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 600,
    flexShrink: 0,
};

const eventBadgeStyle = {
    display: 'inline-block',
    padding: '2px 10px',
    background: '#F3F4F6',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#4B5563',
};

const platesBadgeStyle = {
    display: 'inline-block',
    padding: '2px 10px',
    background: '#FEF3C7',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#92400E',
};

const actionGroupStyle = {
    display: 'flex',
    gap: '6px',
    justifyContent: 'center',
    flexWrap: 'wrap',
};

const viewButtonStyle = {
    padding: '6px 10px',
    background: '#DBEAFE',
    color: '#1D4ED8',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '13px',
};

const sendButtonStyle = {
    padding: '6px 10px',
    background: '#8B5CF6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '13px',
};

const emptyStateStyle = {
    padding: '60px 20px',
    textAlign: 'center',
    borderBottom: 'none',
};

const emptyStateContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
};

// ─── Modal Styles ──────────────────────────────────────────────

const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
};

const modalStyle = {
    background: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

const modalHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #F3F4F6',
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 1,
};

const modalHeaderLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
};

const modalHeaderIconStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #e66d33, #e66d33)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
};

const modalTitleStyle = {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
};

const modalCloseStyle = {
    background: '#F3F4F6',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    color: '#6B7280',
    padding: '6px 10px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
};

const modalBodyStyle = {
    padding: '24px',
};

// ─── View Modal Styles ─────────────────────────────────────────

const quotationHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '10px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '12px',
};

const contactInfoStyle = {
    display: 'flex',
    gap: '16px',
    marginTop: '4px',
    flexWrap: 'wrap',
};

const quotationMetaStyle = {
    fontSize: '13px',
    color: '#6B7280',
};

const quotationEventStyle = {
    display: 'flex',
    gap: '20px',
    padding: '12px 16px',
    background: '#fff',
    borderRadius: '8px',
    border: '1px solid #F3F4F6',
    marginBottom: '16px',
    flexWrap: 'wrap',
};

const quotationEventItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    color: '#374151',
};

const tableWrapperSmallStyle = {
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    overflow: 'hidden',
    marginBottom: '16px',
};

const subTableStyle = {
    width: '100%',
    fontSize: '13px',
    borderCollapse: 'collapse',
};

const subThStyle = {
    padding: '8px 12px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#6B7280',
    background: '#F3F4F6',
    borderBottom: '1px solid #E5E7EB',
};

const subTdStyle = {
    padding: '8px 12px',
    borderBottom: '1px solid #F3F4F6',
    fontSize: '13px',
    color: '#111827',
};

const sectionContainerStyle = {
    marginBottom: '16px',
};

const sectionTitleStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#e66d33',
    marginBottom: '10px',
    fontSize: '15px',
    fontWeight: 600,
    borderBottom: '2px solid #e66d33',
    paddingBottom: '6px',
};

const categoryContainerStyle = {
    marginBottom: '12px',
};

const categoryTitleStyle = {
    fontSize: '13px',
    fontWeight: 600,
    color: '#4B5563',
    marginBottom: '6px',
};

const menuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #F3F4F6',
    marginBottom: '4px',
    background: '#fff',
};

const menuItemVegStyle = {
    borderLeft: '4px solid #48bb78',
};

const menuItemNonVegStyle = {
    borderLeft: '4px solid #fc8181',
};

const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
};

const vegBadgeStyle = {
    background: '#c6f6d5',
    color: '#276749',
};

const nonVegBadgeStyle = {
    background: '#fed7d7',
    color: '#9b2c2c',
};

const addOnItemStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderRadius: '6px',
    background: '#F0FFF4',
    border: '1px solid #C6F6D5',
    marginBottom: '4px',
};

const addOnQtyStyle = {
    background: '#48BB78',
    color: 'white',
    padding: '2px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
};

const additionalRequestStyle = {
    background: '#FEFCBF',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #F6E05E',
    marginBottom: '16px',
    fontSize: '13px',
};

// ─── Form Action Styles ─────────────────────────────────────────

const formActionStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #F3F4F6',
};

const cancelButtonStyle = {
    padding: '10px 24px',
    background: '#F3F4F6',
    color: '#4B5563',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const sendButtonLargeStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 24px',
    background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
};

// ─── Send Modal Styles ──────────────────────────────────────────

const sendEnquiryInfoStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    padding: '12px 16px',
    background: '#F9FAFB',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #E5E7EB',
};

const sendEnquiryInfoItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    fontSize: '13px',
};

const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    marginBottom: '16px',
};

const labelStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
};

const inputStyle = {
    padding: '10px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#fff',
    color: '#111827',
};

const selectAllContainerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    background: '#F9FAFB',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    marginBottom: '12px',
};

const selectAllLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
};

const checkboxStyle = {
    width: '16px',
    height: '16px',
    accentColor: '#e66d33',
    cursor: 'pointer',
};

const vendorListStyle = {
    maxHeight: '250px',
    overflowY: 'auto',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '4px',
    marginBottom: '16px',
};

const vendorItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderBottom: '1px solid #F3F4F6',
};

const noVendorsStyle = {
    padding: '20px',
    textAlign: 'center',
    color: '#9CA3AF',
    fontSize: '14px',
};

const sendAllVendorsButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'center',
    padding: '10px',
    background: '#F3F4F6',
    color: '#4B5563',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    width: '100%',
    marginBottom: '12px',
};

const submitButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 28px',
    background: 'linear-gradient(135deg, #e66d33, #e66d33)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(230, 109, 51, 0.3)',
};

export default Vendordersdetails;